import React, { useState, useEffect, useCallback, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import {
  Warehouse,
  Truck,
  Package,
  Search,
  Settings,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Building2,
  MapPin,
  Pencil,
  Box,
  Filter
} from 'lucide-react';

export default function Warehouses() {
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedWarehouse, setSelectedWarehouse] = useState(null);
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);
  const [editingWarehouse, setEditingWarehouse] = useState(null);
  const [editName, setEditName] = useState('');
  const [editType, setEditType] = useState('warehouse');
  const [editActive, setEditActive] = useState(true);
  // UX: by default show only items with stock (mirrors MyVanStock pattern).
  const [hasStockOnly, setHasStockOnly] = useState(true);
  const PAGE_SIZE = 50;
  const { hasPermission } = useContext(AuthContext);
  const canEditWarehouses = hasPermission('warehouses.edit');

  // Fetch warehouses
  const fetchWarehouses = useCallback(async () => {
    try {
      setLoading(true);
      // Armazéns inactivos só visíveis a quem os pode configurar
      const response = await axios.get(`${API}/warehouses`, { params: { include_inactive: canEditWarehouses } });
      setWarehouses(response.data.warehouses || []);
    } catch (error) {
      console.error('Error fetching warehouses:', error);
      toast.error('Erro ao carregar armazéns');
    } finally {
      setLoading(false);
    }
  }, [canEditWarehouses]);

  const [syncingSage, setSyncingSage] = useState(false);
  const syncFromSage = async () => {
    setSyncingSage(true);
    try {
      const { data } = await axios.post(`${API}/warehouses/sync-sage`);
      toast.success(data.created?.length
        ? `${data.created.length} armazém(ns) novo(s) do SAGE: ${data.created.join(', ')}`
        : `Armazéns atualizados (${data.total} no SAGE, nenhum novo)`);
      fetchWarehouses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao ler armazéns do SAGE');
    } finally {
      setSyncingSage(false);
    }
  };

  // Fetch products for a warehouse
  const fetchWarehouseProducts = useCallback(async (warehouseCode, pageNum = 1, search = '', stockOnly = true) => {
    try {
      setProductsLoading(true);
      const response = await axios.get(`${API}/warehouses/${warehouseCode}/products`, {
        params: {
          page: pageNum,
          page_size: PAGE_SIZE,
          search: search || undefined,
          sort_by: 'sku',
          sort_order: 'asc',
          // UX: armazéns mostram apenas artigos com stock por omissão.
          has_stock: stockOnly || undefined,
        }
      });
      setProducts(response.data.products || []);
      setTotalProducts(response.data.total || 0);
    } catch (error) {
      console.error('Error fetching warehouse products:', error);
      toast.error('Erro ao carregar produtos do armazém');
    } finally {
      setProductsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchWarehouses();
  }, [fetchWarehouses]);

  useEffect(() => {
    if (selectedWarehouse) {
      fetchWarehouseProducts(selectedWarehouse.code, page, searchQuery, hasStockOnly);
    }
  }, [selectedWarehouse, page, searchQuery, hasStockOnly, fetchWarehouseProducts]);

  const handleSelectWarehouse = (warehouse) => {
    setSelectedWarehouse(warehouse);
    setPage(1);
    setSearchQuery('');
  };

  const handleBack = () => {
    setSelectedWarehouse(null);
    setProducts([]);
    setPage(1);
    setSearchQuery('');
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    setPage(1);
  };

  const handleEditWarehouse = (warehouse) => {
    setEditingWarehouse(warehouse);
    setEditName(warehouse.name || '');
    setEditType(warehouse.warehouse_type || warehouse.type || 'outro');
    setEditActive(warehouse.is_active !== false);
  };

  const handleSaveWarehouse = async () => {
    try {
      await axios.put(`${API}/warehouses/${editingWarehouse.code}`, {
        name: editName,
        warehouse_type: editType,
        is_active: editActive,
      });
      toast.success('Armazém actualizado');
      setEditingWarehouse(null);
      fetchWarehouses();
    } catch (error) {
      console.error('Error updating warehouse:', error);
      toast.error(error.response?.data?.detail || 'Erro ao actualizar armazém');
    }
  };

  const getWarehouseIcon = (typeRaw) => {
    // Normalize: accept new warehouse_type values and legacy type values.
    const t = typeRaw === 'van' ? 'carrinha'
            : typeRaw === 'store' ? 'loja'
            : typeRaw === 'warehouse' ? 'central'
            : typeRaw;
    switch (t) {
      case 'carrinha':
        return <Truck className="h-5 w-5 text-blue-500" />;
      case 'loja':
        return <Building2 className="h-5 w-5 text-green-500" />;
      case 'virtual':
        return <Warehouse className="h-5 w-5 text-amber-500" />;
      case 'central':
        return <Warehouse className="h-5 w-5 text-red-600" />;
      default:
        return <Warehouse className="h-5 w-5 text-slate-500" />;
    }
  };

  const getWarehouseTypeBadge = (typeRaw) => {
    const t = typeRaw === 'van' ? 'carrinha'
            : typeRaw === 'store' ? 'loja'
            : typeRaw === 'warehouse' ? 'central'
            : typeRaw;
    switch (t) {
      case 'carrinha':
        return <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">Carrinha</Badge>;
      case 'loja':
        return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">Loja</Badge>;
      case 'virtual':
        return <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">Virtual</Badge>;
      case 'central':
        return <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">Central</Badge>;
      default:
        return <Badge variant="outline" className="bg-slate-50 text-slate-700 border-slate-200">Armazém</Badge>;
    }
  };

  const totalPages = Math.ceil(totalProducts / PAGE_SIZE);

  // Warehouse list view
  if (!selectedWarehouse) {
    return (
      <div className="min-h-screen bg-slate-50 pb-24 md:pb-8">
        {/* Header */}
        <div className="bg-white border-b border-slate-200">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-4 md:py-6">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <h1 className="text-xl md:text-2xl font-bold text-slate-900 flex items-center gap-2">
                  <Warehouse className="h-5 w-5 md:h-6 md:w-6 text-red-600" />
                  Armazéns
                </h1>
                <p className="text-xs sm:text-sm text-slate-500">Gestão de armazéns e carrinhas</p>
              </div>
              <div className="flex gap-2">
                {canEditWarehouses && (
                  <Button onClick={syncFromSage} variant="outline" size="sm" disabled={syncingSage} data-testid="warehouses-sync-sage-btn">
                    <RefreshCw className={`h-4 w-4 sm:mr-2 ${syncingSage ? 'animate-spin' : ''}`} />
                    <span className="hidden sm:inline">Atualizar do SAGE</span>
                  </Button>
                )}
                <Button onClick={fetchWarehouses} variant="outline" size="sm" disabled={loading} data-testid="warehouses-refresh-btn">
                  <RefreshCw className={`h-4 w-4 sm:mr-2 ${loading ? 'animate-spin' : ''}`} />
                  <span className="hidden sm:inline">Actualizar</span>
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-4 md:py-6 space-y-4 sm:space-y-6">
          {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="h-8 w-8 animate-spin text-slate-400" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {warehouses.map((warehouse) => (
              <Card
                key={warehouse.code}
                className={`cursor-pointer hover:shadow-lg transition-shadow border-l-4 ${warehouse.is_active === false ? 'border-l-slate-300 opacity-60' : 'border-l-red-500'}`}
                onClick={() => handleSelectWarehouse(warehouse)}
                data-testid={`warehouse-card-${warehouse.code}`}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {getWarehouseIcon(warehouse.warehouse_type || warehouse.type)}
                      <div>
                        <CardTitle className="text-lg">{warehouse.name}</CardTitle>
                        <CardDescription className="font-mono text-xs">{warehouse.code}</CardDescription>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={(e) => { e.stopPropagation(); handleEditWarehouse(warehouse); }}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {getWarehouseTypeBadge(warehouse.warehouse_type || warehouse.type)}
                      {warehouse.is_active === false && (
                        <Badge variant="outline" className="bg-slate-100 text-slate-500 border-slate-300" data-testid={`inactive-badge-${warehouse.code}`}>Inativo</Badge>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-slate-800">{warehouse.product_count}</p>
                      <p className="text-xs text-slate-500">produtos em stock</p>
                    </div>
                  </div>
                  <div className="mt-3 pt-3 border-t">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-500">Stock Total:</span>
                      <span className="font-semibold">{warehouse.total_stock.toLocaleString('pt-PT')} un</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Edit Warehouse Modal */}
        <Dialog open={!!editingWarehouse} onOpenChange={() => setEditingWarehouse(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Editar Armazém</DialogTitle>
              <DialogDescription>
                Configurar nome e tipo do armazém {editingWarehouse?.code}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Nome</label>
                <Input
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  placeholder="Nome do armazém"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Tipo</label>
                <Select value={editType} onValueChange={setEditType}>
                  <SelectTrigger data-testid="warehouse-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="central">
                      <div className="flex items-center gap-2">
                        <Warehouse className="h-4 w-4 text-red-600" /> Central
                      </div>
                    </SelectItem>
                    <SelectItem value="loja">
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-green-500" /> Loja
                      </div>
                    </SelectItem>
                    <SelectItem value="carrinha">
                      <div className="flex items-center gap-2">
                        <Truck className="h-4 w-4 text-blue-500" /> Carrinha
                      </div>
                    </SelectItem>
                    <SelectItem value="virtual">
                      <div className="flex items-center gap-2">
                        <Warehouse className="h-4 w-4 text-amber-500" /> Virtual / Agregador
                      </div>
                    </SelectItem>
                    <SelectItem value="outro">
                      <div className="flex items-center gap-2">
                        <Warehouse className="h-4 w-4 text-slate-400" /> Outro
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
                {editingWarehouse?.warehouse_type_source === 'inferred' && (
                  <p className="text-xs text-muted-foreground">
                    Tipo inferido automaticamente (regra de classificação). Guarda para fixar como manual.
                  </p>
                )}
              </div>
              <div className="flex items-center justify-between rounded-lg border p-3">
                <div>
                  <p className="text-sm font-medium">Armazém ativo</p>
                  <p className="text-xs text-muted-foreground">
                    Inativo: deixa de aparecer nos seletores de armazém em toda a app.
                  </p>
                </div>
                <Switch
                  checked={editActive}
                  onCheckedChange={setEditActive}
                  data-testid="warehouse-active-toggle"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditingWarehouse(null)}>
                Cancelar
              </Button>
              <Button onClick={handleSaveWarehouse} className="bg-[#C41E3A] hover:bg-[#a01830]">
                Guardar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Warehouse detail view — mobile-first layout that mirrors MyVanStock,
  // with a wider desktop table on ≥md viewports.
  // ─────────────────────────────────────────────────────────────────────────
  const totalUnitsOnPage = products.reduce((sum, p) => sum + (p.warehouse_stock || 0), 0);

  return (
    <>
      {/* ─── MOBILE / SMARTPHONE (< md) ────────────────────────────────── */}
      <div className="md:hidden flex flex-col min-h-[100dvh] -m-3 sm:-m-4" data-testid="warehouse-detail-mobile">
        {/* Sticky header */}
        <div className="sticky top-0 z-10 bg-white border-b shadow-sm">
          <div className="px-3 py-2.5 flex items-center gap-2">
            <button
              onClick={handleBack}
              className="h-9 px-2 flex items-center text-slate-500 active:bg-slate-100 rounded-lg text-sm font-medium"
              data-testid="warehouse-back-btn-mobile"
            >
              <ChevronLeft className="h-4 w-4 mr-0.5" />
              Voltar
            </button>
            <div className="h-9 w-9 rounded-lg bg-slate-50 flex items-center justify-center flex-shrink-0">
              {getWarehouseIcon(selectedWarehouse.warehouse_type || selectedWarehouse.type)}
            </div>
            <div className="min-w-0 flex-1">
              <h1 className="text-sm font-bold text-slate-900 leading-tight truncate" title={selectedWarehouse.name}>
                {selectedWarehouse.name}
              </h1>
              <p className="text-[11px] text-slate-500 font-mono">{selectedWarehouse.code}</p>
            </div>
            {getWarehouseTypeBadge(selectedWarehouse.warehouse_type || selectedWarehouse.type)}
          </div>

          {/* Stats bar */}
          <div className="px-3 pb-2 flex gap-2">
            <div className="flex-1 bg-slate-50 rounded-lg px-3 py-2 flex items-center gap-2">
              <Package className="h-4 w-4 text-slate-400" />
              <div>
                <p className="text-lg font-bold text-slate-800 leading-none">{totalProducts}</p>
                <p className="text-[10px] text-slate-500 uppercase">Referências</p>
              </div>
            </div>
            <div className="flex-1 bg-green-50 rounded-lg px-3 py-2 flex items-center gap-2">
              <Box className="h-4 w-4 text-green-500" />
              <div>
                <p className="text-lg font-bold text-green-700 leading-none">
                  {hasStockOnly ? totalUnitsOnPage : selectedWarehouse.total_stock || 0}
                </p>
                <p className="text-[10px] text-green-600 uppercase">Un. em Stock</p>
              </div>
            </div>
          </div>

          {/* Search + Filter */}
          <div className="px-3 pb-3 flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Pesquisar SKU, nome, EAN..."
                value={searchQuery}
                onChange={handleSearch}
                className="pl-10 h-10 text-sm"
                data-testid="warehouse-search-input-mobile"
              />
            </div>
            <button
              onClick={() => { setHasStockOnly(!hasStockOnly); setPage(1); }}
              className={`h-10 px-3 rounded-lg border text-xs font-medium flex items-center gap-1.5 whitespace-nowrap transition-colors ${
                hasStockOnly
                  ? 'bg-green-50 border-green-200 text-green-700'
                  : 'bg-white border-slate-200 text-slate-500'
              }`}
              data-testid="warehouse-stock-filter-toggle"
            >
              <Filter className="h-3.5 w-3.5" />
              {hasStockOnly ? 'Com Stock' : 'Todos'}
            </button>
          </div>
        </div>

        {/* Product list */}
        <div className="flex-1 overflow-y-auto pb-24">
          {productsLoading && products.length === 0 ? (
            <div className="flex items-center justify-center py-16">
              <RefreshCw className="h-8 w-8 animate-spin text-slate-300" />
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-16 px-6">
              <Package className="h-12 w-12 mx-auto text-slate-300 mb-3" />
              <p className="text-sm font-medium text-slate-500">
                {searchQuery ? 'Nenhum resultado encontrado' : hasStockOnly ? 'Sem artigos em stock' : 'Sem artigos neste armazém'}
              </p>
            </div>
          ) : (
            <>
              <div className="divide-y divide-slate-100" data-testid="warehouse-products-list">
                {products.map((product) => {
                  const whStock = product.warehouse_stock || 0;
                  const totalStock = product.current_stock || 0;
                  return (
                    <div
                      key={product.id || product.sku}
                      className="px-4 py-3 active:bg-slate-50"
                      data-testid={`product-row-${product.sku}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <p className="text-sm font-semibold text-slate-900 leading-snug flex-1 min-w-0">
                          {product.name}
                        </p>
                        <div className="flex-shrink-0 text-right">
                          <span className={`text-xl font-bold tabular-nums ${
                            whStock > 0 ? 'text-green-600' : 'text-slate-300'
                          }`}>
                            {whStock}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between mt-1">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="text-xs font-mono text-slate-400">{product.sku}</span>
                          {product.ean && (
                            <span className="text-xs text-slate-300 truncate max-w-[120px]">{product.ean}</span>
                          )}
                        </div>
                        <span className="text-xs text-slate-400 flex-shrink-0">
                          Total: {totalStock}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-3 pt-4 pb-2 px-4">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page <= 1}
                    className="h-10 px-4 rounded-lg border border-slate-200 text-sm font-medium disabled:opacity-30 active:bg-slate-50"
                  >
                    Anterior
                  </button>
                  <span className="text-sm text-slate-500">{page} / {totalPages}</span>
                  <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages}
                    className="h-10 px-4 rounded-lg border border-slate-200 text-sm font-medium disabled:opacity-30 active:bg-slate-50"
                  >
                    Seguinte
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* ─── DESKTOP (≥ md) ─────────────────────────────────────────────── */}
      <div className="hidden md:block p-3 sm:p-6 space-y-6 pb-24">
        <div className="flex items-center gap-4 flex-wrap">
          <Button variant="ghost" onClick={handleBack} data-testid="warehouse-back-btn">
            <ChevronLeft className="h-4 w-4 mr-1" />
            Voltar
          </Button>
          <div className="flex items-center gap-3 min-w-0 flex-1">
            {getWarehouseIcon(selectedWarehouse.warehouse_type || selectedWarehouse.type)}
            <div className="min-w-0">
              <h1 className="text-2xl font-bold text-slate-800 truncate">{selectedWarehouse.name}</h1>
              <p className="text-slate-500 font-mono text-sm">{selectedWarehouse.code}</p>
            </div>
            {getWarehouseTypeBadge(selectedWarehouse.warehouse_type || selectedWarehouse.type)}
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Produtos</p>
                  <p className="text-2xl font-bold">{totalProducts}</p>
                </div>
                <Package className="h-8 w-8 text-slate-300" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Stock Total</p>
                  <p className="text-2xl font-bold">{(selectedWarehouse.total_stock || 0).toLocaleString('pt-PT')}</p>
                </div>
                <MapPin className="h-8 w-8 text-slate-300" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search + filter (desktop) */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Pesquisar por SKU, nome ou EAN..."
              value={searchQuery}
              onChange={handleSearch}
              className="pl-10"
              data-testid="warehouse-search-input"
            />
          </div>
          <button
            onClick={() => { setHasStockOnly(!hasStockOnly); setPage(1); }}
            className={`h-10 px-3 rounded-lg border text-xs font-medium flex items-center gap-1.5 transition-colors ${
              hasStockOnly
                ? 'bg-green-50 border-green-200 text-green-700'
                : 'bg-white border-slate-200 text-slate-500'
            }`}
            data-testid="warehouse-stock-filter-toggle-desktop"
          >
            <Filter className="h-3.5 w-3.5" />
            {hasStockOnly ? 'Com Stock' : 'Todos'}
          </button>
        </div>

        {/* Products Table */}
        <Card>
          <CardContent className="p-0">
            {productsLoading ? (
              <div className="flex justify-center py-12">
                <RefreshCw className="h-6 w-6 animate-spin text-slate-400" />
              </div>
            ) : products.length === 0 ? (
              <div className="text-center py-12 text-slate-500 text-sm">
                Nenhum produto encontrado
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>SKU</TableHead>
                      <TableHead>Nome</TableHead>
                      <TableHead>EAN</TableHead>
                      <TableHead className="text-right">Disponível</TableHead>
                      <TableHead className="text-right">Cativo (AP)</TableHead>
                      <TableHead className="text-right">Stock Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {products.map((product) => (
                      <TableRow key={product.id} className="hover:bg-slate-50">
                        <TableCell className="font-mono text-sm font-medium">{product.sku}</TableCell>
                        <TableCell className="max-w-xs truncate">{product.name}</TableCell>
                        <TableCell className="font-mono text-sm text-slate-500">
                          {product.ean || '-'}
                        </TableCell>
                        <TableCell className="text-right">
                          <span className={`font-semibold ${(product.available !== undefined ? product.available : product.warehouse_stock) === 0 ? 'text-red-600' : 'text-slate-800'}`}>
                            {product.available !== undefined ? product.available : product.warehouse_stock}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          {(product.reserved || 0) > 0 ? (
                            <span className="font-semibold text-amber-600" title="Consumido em APs ainda não faturados">
                              {product.reserved}
                            </span>
                          ) : (
                            <span className="text-slate-300">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right text-slate-500">
                          {product.current_stock}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t">
                <p className="text-sm text-slate-500">
                  Página {page} de {totalPages} ({totalProducts} produtos)
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
}
